import React from "react";
import type { Localizer } from "@/main/react/Types";
import CreateGroupForm from "@/main/react/group/CreateGroupFormLazy";
import type { GroupDiscussionSubmissionPolicyValue } from "@/main/react/group/GroupDiscussionSubmissionPolicy";
import SimpleDialog, { openSimpleDialog } from "@/main/react/Dialog/SimpleDialog";
import { emitGroupSettingsUpdated } from "@/main/common/EventRegistry";
import { GuideLink } from "@/main/react/guides/GuideLink";

type ShowUpdateGroupSettingsDialogProps = {
    groupPublicId?: string;
    name?: string;
    description?: string;
    welcomeMessage?: string;
    thumbnailUrl?: string;
    bannerUrl?: string;
    mainLocale?: string;
    discussionSubmissionPolicy?: GroupDiscussionSubmissionPolicyValue;
    privateAccess?: boolean;
    sensitive?: boolean;
    /** Owner-only affordances inside the form (P1-B ownership transfer). */
    viewerIsOwner?: boolean;
    localizer: Localizer;
};

export function showUpdateGroupSettingsDialog({
    groupPublicId,
    name = "",
    description = "",
    welcomeMessage = "",
    thumbnailUrl = "",
    bannerUrl = "",
    mainLocale,
    discussionSubmissionPolicy,
    privateAccess = false,
    sensitive = false,
    viewerIsOwner = false,
    localizer,
}: ShowUpdateGroupSettingsDialogProps) {
    const isEditing = Boolean(groupPublicId);

    return openSimpleDialog(({ open, onOpenChange }) => (
        <SimpleDialog
            open={open}
            onOpenChange={onOpenChange}
            title={
                    <div id="update-group-dialog-title">
                    {isEditing
                        ? (localizer("group.settings.title", name || "Group"))
                        : (localizer("create.create-group.page-title"))}
                </div>
            }
            width="6xl"
        >
            {/* Creating only. Someone editing a group they already run has answered these questions;
                the pointer belongs at the moment of first arrival, not on every settings visit. */}
            {!isEditing && (
                <div className="mb-3">
                    <GuideLink
                        localizer={localizer}
                        guide="groups"
                        textKey="guides.link.groups"
                        surface="create-group"
                    />
                </div>
            )}
            <CreateGroupForm
                groupPublicId={groupPublicId}
                name={name}
                description={description}
                welcomeMessage={welcomeMessage}
                thumbnailUrl={thumbnailUrl}
                bannerUrl={bannerUrl}
                mainLocale={mainLocale}
                discussionSubmissionPolicy={discussionSubmissionPolicy}
                privateAccess={privateAccess}
                sensitive={sensitive}
                viewerIsOwner={viewerIsOwner}
                localizer={localizer}
                onCancel={() => onOpenChange(false)}
                onSaved={(result) => {
                    onOpenChange(false);
                    if (isEditing && result?.group?.groupPublicId) {
                        emitGroupSettingsUpdated({
                            groupPublicId: result.group.groupPublicId,
                            name: result.group.name,
                            description: result.group.description ?? null,
                            thumbnailUrl: result.group.thumbnailUrl ?? null,
                            bannerUrl: result.group.bannerUrl,
                            mainLocale: result.group.mainLocale ?? null,
                            discussionSubmissionPolicy: result.group.discussionSubmissionPolicy ?? null,
                            privateAccess: result.group.privateAccess,
                            sensitive: result.group.sensitive,
                        });
                    }
                }}
            />
        </SimpleDialog>
    ));
}

export function showCreateGroupDialog(localizer: Localizer) {
    return showUpdateGroupSettingsDialog({ localizer });
}
